import { cn } from '@/components/ui/utils';
import { Transition } from '@headlessui/react';
import { cva, VariantProps } from 'class-variance-authority';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../button';
import { useIsMobile, useIsTablet } from '../hooks/device.hook';
import { TappIcon } from '../TappIcon';
import { useTappSidebar } from './sidebar.hook';
import { TappSidebarItem, TappSidebarRow } from './SidebarRow';

const logoStyles = cva('', {
  variants: {
    logoPosition: {
      left: 'md:justify-start',
      center: 'md:justify-center',
      right: 'md:justify-end',
    },
  },
  defaultVariants: {
    logoPosition: 'left',
  },
});

export type TappSidebarProps = {
  items: TappSidebarItem[];
  logoUrl?: string;
  logoSmallUrl?: string;
  className?: string;
} & VariantProps<typeof logoStyles>;

export const TappSidebar: React.FC<TappSidebarProps> = ({
  className,
  items,
  logoUrl,
  logoSmallUrl,
  logoPosition = 'left',
}) => {
  const sidebar = useTappSidebar();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  useEffect(() => {
    if (isMobile) {
      sidebar.close();
    } else if (isTablet) {
      sidebar.setCollapsed(true);
    } else {
      sidebar.open();
    }
  }, [isMobile, isTablet]);

  const splitItems = useMemo(() => {
    const top: TappSidebarItem[] = [];
    const bottom: TappSidebarItem[] = [];
    if (items && items.length > 0) {
      items.forEach((i) => {
        if (i.position === 'bottom') {
          bottom.push(i);
        } else {
          top.push(i);
        }
      });
    }
    return { top, bottom };
  }, [items]);

  useEffect(() => {
    if (isMobile && sidebar.isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  }, [isMobile, sidebar]);

  return (
    <Transition
      show={sidebar.isOpen}
      as="div"
      className={cn('fixed bottom-0 left-0 top-0', { 'z-50': isMobile })}
      enter="transition-all duration-500"
      enterFrom="-translate-x-full"
      enterTo="translate-x-0"
      leave="transition-all duration-500"
      leaveFrom="translate-x-0"
      leaveTo="-translate-x-full"
    >
      <aside
        className={cn(
          'group/aside flex h-full flex-col items-start overflow-x-hidden border-r border-r-border bg-background pb-4 pt-6 transition-all duration-500',
          className,
          {
            'w-20': sidebar.isCollapsed,
            'w-64 md:w-56': !sidebar.isCollapsed,
          }
        )}
      >
        <div
          className={cn(
            'grid h-9 w-full grid-cols-[auto_1fr] items-center md:flex',
            logoStyles({ logoPosition: sidebar.isCollapsed ? 'center' : logoPosition }),
            {
              'mb-8 px-6': !sidebar.isCollapsed,
              'mb-8 px-2': sidebar.isCollapsed,
            }
          )}
        >
          <button
            className="mr-5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-sm bg-muted text-secondary-foreground md:hidden"
            onClick={() => sidebar.close()}
          >
            <TappIcon size={16}>close</TappIcon>
          </button>

          {/* TODO: Add animations on enter and leave */}
          <Link to="/admin">
            {logoUrl && (!sidebar.isCollapsed || !logoSmallUrl) && (
              <img className="max-h-9" src={logoUrl} alt="App logo" />
            )}

            {logoSmallUrl && (sidebar.isCollapsed || !logoUrl) && (
              <img className="max-h-9" src={logoSmallUrl} alt="App logo" />
            )}
          </Link>
        </div>

        <section className="flex w-full grow flex-col overflow-y-auto overflow-x-hidden">
          {splitItems.top.length > 0 && (
            <section className="flex w-full grow flex-col">
              {splitItems.top.map((item, index) => (
                <TappSidebarRow key={index} item={item}></TappSidebarRow>
              ))}
            </section>
          )}
          {splitItems.bottom.length > 0 && (
            <section className="mt-auto flex w-full flex-col">
              {splitItems.bottom.map((item, index) => (
                <TappSidebarRow key={index} item={item}></TappSidebarRow>
              ))}
            </section>
          )}
        </section>

        <Button
          size="iconSm"
          variant="outline"
          shape="pill"
          className="absolute bottom-12 right-0 hidden translate-x-4 scale-0 shadow-sm transition-transform group-hover/aside:scale-100 md:inline-flex"
          onClick={() => sidebar.toggleCollapse()}
        >
          <TappIcon size={24} className={cn('transition', { 'rotate-180': sidebar.isCollapsed })}>
            chevron_left
          </TappIcon>
        </Button>
      </aside>
    </Transition>
  );
};
